import Parser from 'html-react-parser';

// Проверка на наличие window (SSR безопасность)
const isBrowser = typeof window !== 'undefined';

const debounce = (fn, delay) => {
  let timeoutId = null;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const throttle = (fn, delay) => {
  let waiting = false;
  return (...args) => {
    if (!waiting) {
      fn(...args);
      waiting = true;
      setTimeout(() => (waiting = false), delay);
    }
  };
};

let elements = [];
let innerWidth = isBrowser ? window.innerWidth : 0;
let scrollY = isBrowser ? window.scrollY : 0;

const getClassAttribute = (element) => {
  return element.getAttribute("class") || "";
};

const setClassAttribute = (element, className) => {
  element.setAttribute("class", className);
};

const setClassNameAfterTrigger = (element) => {
  const className = getClassAttribute(element);

  if (!className.includes("taos-animate")) {
    element.style.transitionDuration = "";
    element.style.transitionDelay = "";

    setClassAttribute(element, className + " taos-animate");
  }
};

const setClassNameBefore = (element) => {
  const className = getClassAttribute(element);
  setClassAttribute(element, className.split(" taos-animate").join(""));
};

const getTrigger = ({ element, offset }) => {
  if (!isBrowser) return 0;
  return (
    element.getBoundingClientRect().top - window.innerHeight + offset + scrollY
  );
};

const refreshTriggers = throttle(() => {
  if (!isBrowser) return;
  elements.forEach((el) => (el.trigger = getTrigger(el)));
}, 250);

const handleScroll = () => {
  if (!isBrowser) return;
  
  scrollY = window.scrollY;

  elements.forEach(({ element, trigger, once, offset }) => {
    trigger ||= getTrigger({ element, offset });

    if (trigger < scrollY) {
      setClassNameAfterTrigger(element);
    } else if (!once && getClassAttribute(element).includes("taos-animate")) {
      setClassNameBefore(element);
    }
  });

  refreshTriggers();
};

const refresh = () => {
  if (!isBrowser) return;
  
  refreshTriggers();
  requestAnimationFrame(handleScroll);
};

const handleResize = () => {
  if (!isBrowser) return;
  
  if (innerWidth !== window.innerWidth) {
    innerWidth = window.innerWidth;
    refresh();
  }
};

const onScroll = throttle(handleScroll, 32);
const onResize = debounce(handleResize, 250);

export const initElement = (element) => {
  // Если не в браузере, ничего не делаем
  if (!isBrowser || !element) return;
  
  let className = getClassAttribute(element);

  if (!className.includes("taos-init")) {
    setClassAttribute(element, className + " taos-init");
  }

  element.style.transitionDuration = "0ms";
  element.style.transitionDelay = "0ms";
  setClassNameBefore(element);

  // Проверяем, не добавлен ли уже этот элемент
  const existingElement = elements.find(el => el.element === element);
  if (!existingElement) {
    elements.push({
      element,
      once: getComputedStyle(element)["animation-iteration-count"] === "1",
      offset: parseInt(element.dataset.taosOffset || 0),
    });
  }

  if (elements.length === 1) {
    window.addEventListener("scroll", onScroll);
    window.addEventListener("orientationchange", refresh);
    window.addEventListener("resize", onResize);
  }

  refresh();
};

export const removeElement = (element) => {
  if (!isBrowser) return;
  
  elements = elements.filter((el) => el.element !== element);

  if (elements.length === 0) {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("orientationchange", refresh);
    window.removeEventListener("resize", onResize);
  }
};

// Добавляем функцию для повторной инициализации (для случаев, когда нужно сбросить анимацию)
export const reinitElement = (element) => {
  if (!isBrowser || !element) return;
  
  setClassNameBefore(element);
  requestAnimationFrame(() => {
    handleScroll();
  });
};

// Добавляем функцию для принудительного обновления всех элементов
export const refreshAll = () => {
  if (!isBrowser) return;
  
  elements.forEach(({ element }) => {
    setClassNameBefore(element);
  });
  refresh();
};

/**
 * Безопасный html-react-parser.
 * Штатный Parser() бросает TypeError: First argument must be a string
 * на null/undefined (например, у ресурса MODX пустой content) —
 * из-за этого падал SSR и весь сайт отдавал 503 «Технические работы».
 */
export const safeParse = (value, options) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    return options === undefined ? Parser(value) : Parser(value, options);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
};